import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Zap, LogOut, User } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, isAdmin, signOut, loading } = useAuth();

  const navItems = [
    { name: "Home", href: "/" },
    { name: "Platforms", href: "/#platforms" },
    { name: "Features", href: "/#features" },
    { name: "Pricing", href: "/#pricing" },
    { name: "Compare", href: "/compare" },
    { name: "About Us", href: "/about" },
    { name: "Contact", href: "/#contact" },
  ];

  return (
    <nav className="fixed top-0 w-full z-50 glass-card border-b border-neon-cyan/10">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2.5 cursor-pointer group" onClick={() => window.location.href = '/'}>
            <div className="w-9 h-9 bg-gradient-to-br from-neon-cyan to-neon-violet rounded-lg flex items-center justify-center shadow-[0_0_20px_hsla(185,100%,55%,0.3)] group-hover:shadow-[0_0_30px_hsla(185,100%,55%,0.5)] transition-shadow">
              <Zap className="w-5 h-5 text-background" />
            </div>
            <span className="text-xl font-bold font-display gradient-text">Valmira</span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-muted-foreground hover:text-neon-cyan transition-colors duration-200 font-medium text-sm tracking-wide relative group"
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-neon-cyan group-hover:w-full transition-all duration-300" />
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            {!loading && user && (
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/blog'}>
                    <User className="w-4 h-4 mr-1" />
                    Admin
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={signOut}>
                  <LogOut className="w-4 h-4 mr-1" />
                  Sign Out
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden border-t border-neon-cyan/10"
            >
              <div className="flex flex-col gap-4 py-4">
                {navItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-muted-foreground hover:text-neon-cyan transition-colors duration-200 font-medium py-2"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </a>
                ))}
                {!loading && user && (
                  <div className="flex flex-col gap-2 pt-4">
                    {isAdmin && (
                      <Button variant="outline" size="sm" onClick={() => { setIsOpen(false); window.location.href = '/admin/blog'; }}>
                        <User className="w-4 h-4 mr-1" /> Admin
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setIsOpen(false); signOut(); }}>
                      <LogOut className="w-4 h-4 mr-1" /> Sign Out
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navigation;
